import { GameFantasySummary, PlayerFantasyScore, PlayerCurrentDayStats } from '../types';

// Use environment variable for API base URL
// Defaults to local API in development, production API otherwise
const getApiBase = () => {
  // Check if environment variable is set
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // Default to local API when running on localhost (development)
  // Use HTTP since HTTPS redirection is disabled in development
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:62671/api';
  }
  
  // Fallback to production API
  return 'https://downunderdynastyapi20250812024722-hbhqc2e6btb7g2av.australiaeast-01.azurewebsites.net/api';
};

const API_BASE = getApiBase();

export const nblService = {
  // Get all games with team information
  async getAllGames(): Promise<GameFantasySummary[]> {
    const response = await fetch(`${API_BASE}/Scores/Games`);
    if (!response.ok) {
      throw new Error('Failed to fetch games');
    }
    const games = await response.json();
    
    // Map the response to include gameId for backward compatibility
    return games.map((game: any) => ({
      ...game,
      gameId: game.id, // Map id to gameId for backward compatibility
      // Legacy properties for backward compatibility
      homeTeam: game.homeTeamName,
      awayTeam: game.awayTeamName,
      homeScore: game.homeTeamScore,
      awayScore: game.awayTeamScore,
      gameStatus: game.status,
      // Legacy game properties - map from API response if available
      quarter: game.quarter || undefined,
      timeRemaining: game.timeRemaining || undefined,
      gameTitle: `${game.awayTeamName} @ ${game.homeTeamName}` // Generate game title
    }));
  },

  // Get games for a specific date with team information
  async getGamesByDate(date: Date): Promise<GameFantasySummary[]> {
    const dateStr = date.toISOString().split('T')[0];
    const response = await fetch(`${API_BASE}/Scores/Games/date/${dateStr}`);
    if (!response.ok) {
      if (response.status === 404) {
        return []; // No games found for this date
      }
      throw new Error('Failed to fetch games by date');
    }
    const games = await response.json();
    
    // Map the response to include gameId for backward compatibility
    return games.map((game: any) => ({
      ...game,
      gameId: game.id, // Map id to gameId for backward compatibility
      // Legacy properties for backward compatibility
      homeTeam: game.homeTeamName,
      awayTeam: game.awayTeamName,
      homeScore: game.homeTeamScore,
      awayScore: game.awayTeamScore,
      gameStatus: game.status,
      // Legacy game properties - map from API response if available
      quarter: game.quarter || undefined,
      timeRemaining: game.timeRemaining || undefined,
      gameTitle: `${game.awayTeamName} @ ${game.homeTeamName}` // Generate game title
    }));
  },

  // Get a specific game with team information
  async getGameById(id: number): Promise<GameFantasySummary> {
    // First, get the game info directly
    const gameResponse = await fetch(`${API_BASE}/Scores/Games/${id}`);
    if (!gameResponse.ok) {
      if (gameResponse.status === 404) {
        throw new Error('Game not found');
      }
      throw new Error('Failed to fetch game');
    }
    const game = await gameResponse.json();
    
    // Then get player stats for this game
    const playerStatsResponse = await fetch(`${API_BASE}/Scores/Games/${id}/PlayerStats`);
    let playerStats: any[] = [];
    if (playerStatsResponse.ok) {
      playerStats = await playerStatsResponse.json();
    }
    
    // Return a combined game summary with player stats
    return {
      ...game,
      gameId: game.id, // Map id to gameId for backward compatibility
      // Legacy properties for backward compatibility
      homeTeam: game.homeTeamName,
      awayTeam: game.awayTeamName,
      homeScore: game.homeTeamScore,
      awayScore: game.awayTeamScore,
      gameStatus: game.status,
      // Legacy game properties - map from API response if available
      quarter: game.quarter || undefined,
      timeRemaining: game.timeRemaining || undefined,
      gameTitle: `${game.awayTeamName} @ ${game.homeTeamName}`, // Generate game title
      playerStats: playerStats
    };
  },

  // Get all player statistics for a specific game
  async getPlayerGamesByGameId(gameId: number): Promise<PlayerFantasyScore[]> {
    const response = await fetch(`${API_BASE}/Scores/Games/${gameId}/PlayerStats`);
    if (!response.ok) {
      if (response.status === 404) {
        return []; // No player games found for this game
      }
      throw new Error('Failed to fetch player games for game');
    }
    const playerStats = await response.json();
    
    // Map to include legacy properties for backward compatibility
    return playerStats.map((player: any) => ({
      ...player,
      // Legacy properties for backward compatibility
      team: player.playerTeam,
      position: player.playerPosition,
      fantasyScore: player.fantasyPoints,
      gameId: player.gameId
    }));
  },

  // Get player statistics for a specific team in a game
  async getPlayerGamesByGameAndTeam(gameId: number, team: string): Promise<PlayerFantasyScore[]> {
    const allPlayers = await this.getPlayerGamesByGameId(gameId);
    return allPlayers.filter((player: PlayerFantasyScore) => player.playerTeam === team);
  },

  // Get live games
  async getLiveGames(): Promise<GameFantasySummary[]> {
    const response = await fetch(`${API_BASE}/Scores/LiveGames`);
    if (!response.ok) {
      throw new Error('Failed to fetch live games');
    }
    return response.json();
  },

  // Get fantasy team games with details
  async getFantasyTeamGames(fantasyTeamId: number): Promise<any[]> {
    const response = await fetch(`${API_BASE}/Scores/FantasyTeam/${fantasyTeamId}/Games`);
    if (!response.ok) {
      throw new Error('Failed to fetch fantasy team games');
    }
    return response.json();
  },

  // Get current day fantasy points for players
  async getCurrentDayPlayerStats(playerIds: number[]): Promise<PlayerCurrentDayStats[]> {
    const today = new Date();
    return this.getPlayerStatsForDate(playerIds, today);
  },

  // Get player stats for a specific date
  async getPlayerStatsForDate(playerIds: number[], date: Date): Promise<PlayerCurrentDayStats[]> {
    try {
      // Get games for the specified date
      const dateGames = await this.getGamesByDate(date);
      
      const dayStats: PlayerCurrentDayStats[] = [];
      
      // For each game on that date, get player stats
      for (const game of dateGames) {
        if (game.status.toLowerCase() === 'live' || game.status.toLowerCase() === 'final') {
          const playerStats = await this.getPlayerGamesByGameId(game.id);
          
          // Filter for players we're interested in
          const relevantPlayers = playerStats.filter(player => 
            playerIds.includes(player.playerId)
          );
          
          // Add to day stats
          relevantPlayers.forEach(player => {
            dayStats.push({
              playerId: player.playerId,
              playerName: player.playerName,
              position: player.playerPosition,
              team: player.playerTeam,
              fantasyPoints: player.fantasyPoints,
              gameDate: game.gameDate,
              gameStatus: game.status,
              homeTeam: game.homeTeamName,
              awayTeam: game.awayTeamName,
              hasPlayedToday: true
            });
          });
        }
      }
      
      return dayStats;
    } catch (error) {
      console.error('Error fetching player stats for date:', error);
      return [];
    }
  },

  // Enhanced endpoints with fantasy scores (aliases for consistency)
  async getGamesWithFantasyScores(): Promise<GameFantasySummary[]> {
    return this.getAllGames();
  },

  async getGamesByDateWithFantasyScores(date: Date): Promise<GameFantasySummary[]> {
    return this.getGamesByDate(date);
  },

  async getGameFantasyScores(gameId: number): Promise<PlayerFantasyScore[]> {
    return this.getPlayerGamesByGameId(gameId);
  },

  async getGameFantasySummary(gameId: number): Promise<GameFantasySummary> {
    return this.getGameById(gameId);
  }
};
